import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Info, Settings } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

interface StatusData {
  name: string;
  value: number;
  count: number;
  fill: string;
  status?: string; // Original status value for navigation
  [key: string]: string | number | undefined; // Index signature for Recharts compatibility
}

interface AssetsByStatusCardProps {
  readonly data: StatusData[];
  readonly className?: string;
}

// Green color palette with subtle variance
const GREEN_COLORS = [
  '#10b981', // emerald-500
  '#059669', // emerald-600
  '#34d399', // emerald-400
  '#047857', // emerald-700
  '#6ee7b7', // emerald-300
];

export function AssetsByStatusCard({
  data,
  className,
}: AssetsByStatusCardProps): React.ReactElement {
  const navigate = useNavigate();
  const [metric, setMetric] = useState<'count' | 'value'>('count');

  const hasData = Array.isArray(data) && data.length > 0;

  // Assign green colors to each status
  const dataWithGreenColors = hasData 
    ? data.map((item, index) => ({
        ...item,
        color: GREEN_COLORS[index % GREEN_COLORS.length]
      }))
    : [];

  // Calculate max value for scaling based on selected metric - extracted to avoid nested ternary (S3358)
  const getMetricValue = (d: StatusData): number => metric === 'count' ? d.count : d.value;
  const maxValue = hasData ? Math.max(...dataWithGreenColors.map(getMetricValue)) : 0;
  const totalValue = dataWithGreenColors.reduce((sum, d) => sum + (metric === 'count' ? d.count : d.value), 0);
  const topStatus = hasData ? dataWithGreenColors.reduce((max, item) => {
    const itemMetric = metric === 'count' ? item.count : item.value;
    const maxMetric = metric === 'count' ? max.count : max.value;
    return itemMetric > maxMetric ? item : max;
  }, dataWithGreenColors[0]) : undefined;

  // Handle bar click to navigate to filtered assets
  const handleStatusClick = (status?: string) => {
    if (status) {
      navigate(`/assets?status=${encodeURIComponent(status)}`);
    }
  };

  if (!hasData) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full min-h-[400px] flex flex-col', className)}>
        <CardHeader className="relative">
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Assets by Status</CardTitle>
              <CardDescription>Distribution of asset statuses</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="text-sm text-muted-foreground">No status data available.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full min-h-[400px] flex flex-col', className)}>
      <CardHeader className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Assets by Status</CardTitle>
            <CardDescription>Distribution of asset statuses</CardDescription>
          </div>
          <div className="flex gap-1 items-center">
            <Select value={metric} onValueChange={(value: 'count' | 'value') => setMetric(value)}>
              <SelectTrigger className="h-7 w-[100px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="count">Count</SelectItem>
                <SelectItem value="value">Value</SelectItem>
              </SelectContent>
            </Select>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80">
                    <Info className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    This chart shows how your assets are distributed across different statuses: 
                    Active (currently in use), Pending (awaiting deployment), Inactive (not in use), 
                    Maintenance (under repair or service), and Disposed (sold or recycled).
                    <br /><br />
                    <strong>Click on any bar</strong> to view assets for that specific status.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-6">
          {/* Total Assets Display */}
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-bold">
              {metric === 'count' 
                ? totalValue 
                : new Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(totalValue)
              }
            </div>
            <div className="text-sm text-muted-foreground">
              {metric === 'count' ? 'Total Assets' : 'Total Asset Value'}
            </div>
          </div>

          {/* Bar Chart */}
          <div className="space-y-4">
            {dataWithGreenColors.map((item) => {
              const currentValue = metric === 'count' ? item.count : item.value;
              const percentage = maxValue > 0 ? (currentValue / maxValue) * 100 : 0;
              
              return (
                <button 
                  type="button"
                  key={`status-${item.name}`} 
                  className="space-y-2 cursor-pointer group w-full text-left"
                  onClick={() => handleStatusClick(item.status)}
                  aria-label={`View ${item.name} assets`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-3 w-3 rounded-full transition-transform group-hover:scale-110" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="font-medium group-hover:text-foreground transition-colors">{item.name}</span>
                    </div>
                    <span className="font-semibold group-hover:text-foreground transition-colors">
                      {metric === 'count' 
                        ? `${item.count} assets` 
                        : new Intl.NumberFormat('en-GB', {
                            style: 'currency',
                            currency: 'GBP',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(item.value)
                      }
                    </span>
                  </div>
                  <div className="relative h-8 w-full overflow-hidden rounded-md bg-muted group-hover:bg-muted/80 transition-colors">
                    <div
                      className="h-full transition-all duration-500 ease-out group-hover:brightness-110"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Most Common</p>
              <p className="text-sm font-semibold">
                {topStatus?.name ?? '—'}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Statuses</p>
              <p className="text-sm font-semibold">{dataWithGreenColors.length} types</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
